import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Alert, Card, Select } from "@jhool-io/fe-components";
import { CPT_CODES_DESCRIPTION } from "../../../../utils/constants";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import useToast from "../../../../hooks/useToast";
import {
    AppointmentLocation,
    SessionPlaceOfServiceCode,
} from "../../../../utils/types/session";
import {
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import { useChangeCptCode } from "../../../../hooks/mutations/note";
import {
    INote,
    ModifierTypes,
    NoteLabels,
    NoteTypes,
} from "../../../../modules/notes/types/notes.types";
import { getCptCodeToDisplay } from "../../../../modules/notes/helpers/notes.helpers";
import {
    getAdditionalCptLabel,
    getAdditionalCptCodes,
    getPrimaryCptLabel,
    getPrimaryCptCodes,
    noteTypesWithoutCptCode,
    NoteCptContext,
} from "../../../../modules/notes/helpers/noteCptCodes";

interface ChangeCodeProps {
    note?: INote;
    // Array of notes for bulk change code flow
    notes?: INote[];
    // Should the note details be visible or not
    showDetails?: boolean;
    // Function to call when form submit button is clicked
    onFormSubmit(): void;
}

interface ICreateChangeCodePayload {
    cpt_code: string;
    modifier_codes?: string[];
    pos_code?: string;
}

type Options = {
    label: string;
    value: string;
};

const modifiersTypesForSelect: Options[] = Object.values(ModifierTypes).map(
    (item) => ({
        value: item,
        label: item,
    })
);

const PlaceOfServiceOptions = Object.values(SessionPlaceOfServiceCode).map(
    (place) => ({
        value: place,
        label: place,
    })
);

export default function ChangeCode({
    showDetails = true,
    notes,
    note,
    onFormSubmit,
}: ChangeCodeProps) {
    // Toast for success and error states
    const { toast } = useToast();
    // Change cpt code mutation hook
    const changeCptCode = useChangeCptCode();

    // Query client
    const queryClient = useQueryClient();

    // Get note id  and client id from url params
    const params = useParams();
    const selectedNotes = notes?.filter(Boolean) ?? [];
    const noteToEditId = note?.note_id ?? params.noteId ?? "";
    const clientId = note?.client.client_id ?? params.clientId ?? "";

    // Get note details from cache
    const { data, isFetching, error } = useFetchSessionNote(
        clientId,
        noteToEditId,
        Boolean(clientId && noteToEditId) && selectedNotes.length === 0
    );

    let contexts: NoteCptContext[] = selectedNotes;
    if (!selectedNotes.length && data?.data) {
        contexts = [{ ...data.data, note_type: data.data.type }];
    } else if (!selectedNotes.length && note) {
        contexts = [note];
    }
    const allowedCodes = contexts.length
        ? getPrimaryCptCodes(contexts[0]).filter((code) =>
              contexts.every((item) => getPrimaryCptCodes(item).includes(code))
          )
        : [];
    const cptOptions = [...allowedCodes]
        .sort((a, b) => Number(a) - Number(b))
        .map((code) => ({
            label: `${removeEnumUnderscore(code)} - ${
                CPT_CODES_DESCRIPTION[code] ?? code
            }`,
            value: code,
        }));
    const primaryCptLabel =
        contexts.length === 1 ? getPrimaryCptLabel(contexts[0]) : "CPT CODE";
    const additionalCptLabel =
        contexts.length === 1 ? getAdditionalCptLabel(contexts[0]) : null;
    const primaryCptFieldLabel =
        primaryCptLabel === "E/M CPT CODE" ? "E/M CPT Code" : "CPT Code";
    const additionalCptCodes =
        contexts.length === 1 ? getAdditionalCptCodes(contexts[0]) : [];
    const additionalCptOptions = additionalCptCodes.map((code) => ({
        label: `${code} - ${CPT_CODES_DESCRIPTION[code]}`,
        value: code,
    }));
    const selectedAdditionalCptCode = contexts[0]?.additional_cpt_codes?.[0];
    const isNutritionNote =
        contexts.length === 1 &&
        contexts[0].note_type === NoteTypes.NUTRITION_NOTE_MED;
    const nutritionUnitCalculation = isNutritionNote
        ? data?.data.note_metadata?.unit_calculation
        : undefined;
    const hasNoCptCode =
        contexts.length > 0 &&
        contexts.every((item) =>
            noteTypesWithoutCptCode.includes(item.note_type)
        );

    let editError: string | undefined;

    if (isFetching) {
        editError = "Loading note details…";
    } else if (error) {
        editError =
            "Could not load note details. Please reload before changing codes.";
    } else if (!contexts.length) {
        editError = "Note details are required before changing codes.";
    } else if (!hasNoCptCode && !allowedCodes.length) {
        editError = "No CPT code is valid for this selection.";
    }

    const noteToEditCptCode = data?.data.cpt_code ?? note?.cpt_code;
    let initialCode = noteToEditCptCode ?? "";

    if (selectedNotes.length) {
        initialCode = contexts.every(
            (item) => item.cpt_code === contexts[0].cpt_code
        )
            ? contexts[0]?.cpt_code ?? ""
            : "";
    }

    const schema = yup.object({
        cpt_code: hasNoCptCode
            ? yup.string()
            : yup
                  .string()
                  .required("CPT code is required")
                  .oneOf(
                      allowedCodes,
                      "Select a valid CPT code for this note type"
                  ),
    });

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors, dirtyFields },
    } = useForm<ICreateChangeCodePayload>({
        resolver: yupResolver(schema),
        mode: "onChange",
        values: {
            cpt_code: initialCode,
            modifier_codes:
                data?.data.modifier_codes ?? note?.modifier_codes ?? [],
            pos_code: data?.data.pos_code ?? undefined,
        },
    });

    const modifierCodesFromForm = watch("modifier_codes") ?? [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getAppointmentLocationToShow = (content: { [key: string]: any }) => {
        if (!content.appointment_location) return "--";

        if (AppointmentLocation.TELETHERAPY === content.appointment_location) {
            if (
                content.delivery_option &&
                content.delivery_option === "Other"
            ) {
                return content.other_delivery_option;
            }
            if (
                content.delivery_option &&
                content.delivery_option !== "Other"
            ) {
                return content.delivery_option;
            }
        }
        return removeEnumUnderscore(content.appointment_location);
    };

    const canEditPlaceOfService =
        (!notes || (notes && notes.length === 0)) && data?.data.note_content;

    // Handle form submit
    const onSubmit = (payload: ICreateChangeCodePayload) => {
        if (editError) {
            toast({ mode: "error", message: editError });
            return;
        }
        interface Payload {
            [noteId: string]: {
                cpt_code: string;
                modifier_codes?: string[];
                pos_code?: string;
                confirm_code: boolean;
            };
        }

        const modifierCode = (payload.modifier_codes ?? []).map(
            (item) => item.split(" ")[0]
        );
        const changeCodePayload: Payload = selectedNotes.length
            ? Object.fromEntries(
                  selectedNotes.map((selectedNote) => [
                      selectedNote.note_id,
                      {
                          cpt_code: payload.cpt_code,
                          confirm_code: false,
                          modifier_codes: dirtyFields.modifier_codes
                              ? modifierCode
                              : selectedNote.modifier_codes ?? [],
                      },
                  ])
              )
            : {
                  [noteToEditId]: {
                      ...payload,
                      confirm_code: false,
                      modifier_codes: modifierCode,
                  },
              };

        changeCptCode.mutate(changeCodePayload, {
            onSuccess: (response) => {
                queryClient.invalidateQueries({
                    queryKey: selectedNotes.length
                        ? undefined
                        : [clientId, "session-note", noteToEditId],
                    predicate: selectedNotes.length
                        ? (query) =>
                              query.queryKey[1] === "session-note" &&
                              selectedNotes.some(
                                  (item) => item.note_id === query.queryKey[2]
                              )
                        : undefined,
                });
                queryClient.invalidateQueries({
                    queryKey: ["notes"],
                });
                queryClient.invalidateQueries({
                    queryKey: selectedNotes.length
                        ? ["get-claim"]
                        : ["get-claim", { note_id: noteToEditId }],
                });
                toast({
                    mode: "success",
                    message:
                        response.message ?? "Cpt code updated successfully",
                });
                onFormSubmit();
            },

            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ??
                        "Could not change code at this time",
                });
            },
        });
    };

    return (
        <>
            {showDetails && (
                <Card classNames="mb-[--spacing-header]">
                    <h2 className="text-base mb-[--spacing-header]">
                        NOTE DETAILS
                    </h2>
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-[--spacing-items]">
                        <div>
                            <p className="section-info">{primaryCptLabel}</p>
                            <p className="section-value">
                                {getCptCodeToDisplay({
                                    noteType: data?.data.type as NoteTypes,
                                    cptCode: noteToEditCptCode,
                                })}
                            </p>
                        </div>

                        {additionalCptLabel && (
                            <div>
                                <p className="section-info">
                                    {additionalCptLabel}
                                </p>
                                <p className="section-value">
                                    {selectedAdditionalCptCode ?? "--"}
                                </p>
                            </div>
                        )}

                        <div>
                            <p className="section-info">NOTE TYPE</p>
                            <p className="section-value capitalize">
                                {makeStringFirstLetterCapital(
                                    removeEnumUnderscore(
                                        NoteLabels[data?.data.type as NoteTypes]
                                    )
                                )}
                            </p>
                        </div>

                        <div>
                            <p className="section-info">APPOINTMENT TYPE</p>
                            <p className="section-value capitalize">
                                {removeEnumUnderscore(
                                    data?.data.appointment_type || "--"
                                )}
                            </p>
                        </div>
                        <div>
                            <p className="section-info">PROVIDER NAME</p>
                            <p className="section-value">
                                {data?.data.provider
                                    ? `${data.data.provider.first_name} ${data.data.provider.last_name}`
                                    : "--"}
                            </p>
                        </div>
                        {canEditPlaceOfService && (
                            <div>
                                <p className="section-info">
                                    Appointment location
                                </p>
                                <p className="section-value capitalize">
                                    {getAppointmentLocationToShow(
                                        data.data.note_content
                                    )}
                                </p>
                            </div>
                        )}
                    </div>
                </Card>
            )}

            <form
                id="change-cpt-code"
                aria-label="form"
                onSubmit={handleSubmit(onSubmit)}
            >
                {editError && <Alert type="warning" description={editError} />}
                {!hasNoCptCode && (
                    <div className="fg">
                        <Controller
                            name="cpt_code"
                            control={control}
                            defaultValue={
                                cptOptions.find(
                                    (code) => code.value === noteToEditCptCode
                                )?.value
                            }
                            render={({ field }) => (
                                <Select
                                    label={primaryCptFieldLabel}
                                    placeholder={primaryCptFieldLabel}
                                    options={cptOptions}
                                    isDisabled={
                                        Boolean(editError) || isNutritionNote
                                    }
                                    onChange={(val) =>
                                        field.onChange(
                                            (val as Options | null)?.value ?? ""
                                        )
                                    }
                                    isSearchable
                                    value={
                                        cptOptions.find(
                                            (code) => code.value === field.value
                                        ) ?? null
                                    }
                                    hasError={!!errors.cpt_code}
                                    errorText={errors.cpt_code?.message}
                                    isLongListInDialog
                                />
                            )}
                        />
                    </div>
                )}
                {nutritionUnitCalculation && (
                    <div className="fg grid grid-cols-3 gap-[--spacing-items]">
                        <div>
                            <p className="section-info">DURATION</p>
                            <p className="section-value">
                                {nutritionUnitCalculation.duration} minutes
                            </p>
                        </div>
                        <div>
                            <p className="section-info">MINS PER UNIT</p>
                            <p className="section-value">
                                {nutritionUnitCalculation.minutes_per_unit}
                            </p>
                        </div>
                        <div>
                            <p className="section-info">TOTAL UNITS</p>
                            <p className="section-value">
                                {nutritionUnitCalculation.total_units}
                            </p>
                        </div>
                    </div>
                )}
                {additionalCptOptions.length ? (
                    <div className="fg">
                        <Select
                            label={additionalCptLabel ?? "Additional CPT code"}
                            placeholder="Automatically calculated"
                            options={additionalCptOptions}
                            value={
                                additionalCptOptions.find(
                                    (code) =>
                                        code.value === selectedAdditionalCptCode
                                ) ?? null
                            }
                            isDisabled
                            isLongListInDialog
                        />
                    </div>
                ) : (
                    contexts.map((item, index) =>
                        item.additional_cpt_codes?.length ? (
                            <p
                                key={
                                    selectedNotes[index]?.note_id ??
                                    noteToEditId
                                }
                                className="mb-4"
                            >
                                Backend-calculated add-ons:{" "}
                                {item.additional_cpt_codes.join(", ")}
                            </p>
                        ) : null
                    )
                )}

                <div className="fg">
                    <Controller
                        name="modifier_codes"
                        control={control}
                        defaultValue={data?.data.modifier_codes}
                        render={({ field }) => (
                            <Select
                                label="Modifier code"
                                placeholder="Modifier code"
                                options={modifiersTypesForSelect}
                                onChange={(val) => {
                                    field.onChange(
                                        ((val as Options[] | null) ?? []).map(
                                            (code) => code.value
                                        )
                                    );
                                }}
                                isSearchable
                                value={modifiersTypesForSelect.filter((code) =>
                                    modifierCodesFromForm.some(
                                        (modifier) =>
                                            modifier.split(" ")[0] ===
                                            code.value.split(" ")[0]
                                    )
                                )}
                                isMulti
                                multiHasValues={
                                    modifierCodesFromForm &&
                                    modifierCodesFromForm.length > 0
                                }
                                isLongListInDialog
                                closeMenuOnSelect
                                hideSelectedOptions={false}
                                hasError={!!errors.modifier_codes}
                                errorText={errors.modifier_codes?.message}
                            />
                        )}
                    />
                </div>
                {canEditPlaceOfService && (
                    <Controller
                        name="pos_code"
                        control={control}
                        defaultValue={
                            PlaceOfServiceOptions.find(
                                (pos) => pos.value === data.data.pos_code
                            )?.value
                        }
                        render={({ field }) => (
                            <Select
                                label="POS code"
                                placeholder="POS code"
                                options={PlaceOfServiceOptions}
                                onChange={(val) =>
                                    field.onChange(
                                        (val as Options | null)?.value
                                    )
                                }
                                value={
                                    PlaceOfServiceOptions.find(
                                        (pos) => pos.value === field.value
                                    ) ?? null
                                }
                                isSearchable
                                isLongListInDialog
                            />
                        )}
                    />
                )}
            </form>
        </>
    );
}
